import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
// ADICIONADO: setDoc e serverTimestamp para criar o documento do terapeuta
import { doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore'; 
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, Check } from 'lucide-react';

type SelectableRole = 'paciente' | 'terapeuta';

const CompleteProfile: React.FC = () => {
  // CORREÇÃO: Importa a função refreshAuthUser do AuthContext
  const { user, isLoading: authLoading, refreshAuthUser } = useAuth();
  const [selectedRole, setSelectedRole] = useState<SelectableRole>('paciente');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (user) {
      setIsLoading(true);
      try {
        const userDocRef = doc(db, 'users', user.uid);
        // 1. Atualiza a role na coleção 'users'
        await updateDoc(userDocRef, { role: selectedRole });

        // --- MODIFICAÇÃO ADICIONADA AQUI ---
        // 2. Se a role selecionada for 'terapeuta', cria o documento na coleção 'therapists'
        if (selectedRole === 'terapeuta') {
          console.log(`Utilizador ${user.uid} selecionou o perfil de terapeuta. A criar documento...`);
          const therapistRef = doc(db, 'therapists', user.uid);
          await setDoc(therapistRef, {
            userId: user.uid,
            nomePublico: user.name || 'Terapeuta', // Usa o nome do perfil Google
            email: user.email,
            horasDisponiveis: 0, // Valor inicial padrão
            horariosDisponiveis: {}, // Objeto vazio como valor inicial
            createdAt: serverTimestamp() // Usa o timestamp do servidor
          });
          console.log('Documento do terapeuta criado com sucesso na coleção therapists.');
        }
        // --- FIM DA MODIFICAÇÃO ---
        
        // CORREÇÃO CRUCIAL: Força a atualização do estado de autenticação
        // antes de fazer o redirecionamento
        console.log('A atualizar estado do utilizador...');
        await refreshAuthUser();
        console.log('Estado do utilizador atualizado com sucesso.');
        
        // Redireciona todos os utilizadores para o painel de controlo
        navigate('/painel');
      } catch (error) {
        console.error("Erro ao atualizar perfil:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  if (authLoading || !user) {
    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg p-8 sm:p-12 text-center">
        
        <img src="/assets/logo.png" alt="Logo" className="h-20 w-20 mx-auto mb-6" />

        <h1 className="text-3xl font-bold text-gray-800">Quase lá, {user.name?.split(' ')[0]}!</h1>
        <p className="text-gray-600 mt-2 mb-8">Para finalizar seu cadastro, por favor, nos diga como você usará a plataforma.</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          {/* Opção Paciente */}
          <button
            onClick={() => setSelectedRole('paciente')}
            className={`relative p-6 border-2 rounded-xl text-left transition-all duration-200 ${
              selectedRole === 'paciente' 
                ? 'border-teal-500 bg-teal-50 scale-105 shadow-lg' 
                : 'border-gray-300 hover:border-teal-400'
            }`}
          >
            <User className="h-8 w-8 text-teal-600 mb-3" />
            <h3 className="font-bold text-lg text-gray-800">Sou Paciente</h3>
            <p className="text-sm text-gray-600">Quero participar de sessões e constelar meus temas.</p>
            {selectedRole === 'paciente' && (
              <div className="absolute top-3 right-3 bg-teal-500 text-white rounded-full p-1">
                <Check size={16} />
              </div>
            )}
          </button>

          {/* Opção Terapeuta */}
          <button
            onClick={() => setSelectedRole('terapeuta')}
            className={`relative p-6 border-2 rounded-xl text-left transition-all duration-200 ${
              selectedRole === 'terapeuta' 
                ? 'border-purple-500 bg-purple-50 scale-105 shadow-lg' 
                : 'border-gray-300 hover:border-purple-400'
            }`}
          >
            <Briefcase className="h-8 w-8 text-purple-600 mb-3" />
            <h3 className="font-bold text-lg text-gray-800">Sou Terapeuta</h3>
            <p className="text-sm text-gray-600">Quero conduzir sessões e atender meus clientes.</p>
            {selectedRole === 'terapeuta' && (
              <div className="absolute top-3 right-3 bg-purple-500 text-white rounded-full p-1">
                <Check size={16} />
              </div>
            )}
          </button>
        </div>

        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full max-w-xs mx-auto bg-teal-600 text-white font-bold py-3 px-4 rounded-full hover:bg-teal-700 disabled:bg-teal-400 transition-all transform hover:scale-105 flex items-center justify-center"
        >
          {isLoading && (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          )}
          Confirmar e Continuar
        </button>
      </div>
    </div>
  );
};

export default CompleteProfile;